import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import Icon from "../components/Icon";

/* =========================================================================
   AVISOS
   Un solo mecanismo para toda la app. En la app móvil conviven tres estilos
   (diálogo propio, Alert nativo y alert() del navegador con el título
   literal "Alert"); acá se evita repetir ese error desde el principio.
   ======================================================================== */

const ToastCtx = createContext(null);

const ICONS = { ok: "check", bad: "alert", info: "info" };

export function ToastProvider({ children }) {
  const [items, setItems] = useState([]);
  const seq = useRef(0);

  const dismiss = useCallback((id) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (message, tone = "info", ms = 4200) => {
      const id = ++seq.current;
      setItems((prev) => [...prev, { id, message, tone }]);
      if (ms > 0) setTimeout(() => dismiss(id), ms);
      return id;
    },
    [dismiss],
  );

  const api = useMemo(
    () => ({
      push,
      dismiss,
      ok: (m) => push(m, "ok"),
      error: (m) => push(m, "bad", 6000),
      info: (m) => push(m, "info"),
    }),
    [push, dismiss],
  );

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className="toast">
            <span className={`toast-${t.tone}`} style={{ marginTop: 1 }}>
              <Icon name={ICONS[t.tone] ?? "info"} size={16} />
            </span>
            <span className="toast-msg">{t.message}</span>
            <button
              className="btn btn-ghost btn-icon"
              style={{ width: 24, height: 24, padding: 0 }}
              onClick={() => dismiss(t.id)}
              aria-label="Cerrar aviso"
            >
              <Icon name="close" size={13} />
            </button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export const useToast = () => {
  const ctx = useContext(ToastCtx);
  if (!ctx)
    throw new Error("useToast necesita estar dentro de <ToastProvider>");
  return ctx;
};
